import { useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Send, Phone, Smartphone, AlertTriangle, UserPlus, Pencil, Check, X, Copy } from 'lucide-react';
import { toast } from 'sonner';

function normalizeForCompare(num) {
  if (!num) return '';
  const digits = String(num).replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return num.startsWith('+') ? num : (digits ? `+${digits}` : '');
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function MessagingChat({ number, contactName, smsStatus, onSent, onContactChange }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const normalized = normalizeForCompare(number);

  // TEMA 4.3 — Alian: from the message thread, either create the customer
  // (phone already filled in) with just a name, or edit the existing
  // contact's name inline, without leaving the conversation.
  const [contact, setContact] = useState(null);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveForm, setSaveForm] = useState({ first_name: '', last_name: '', account_type: 'residential' });
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '' });

  const lookupContact = useCallback(async () => {
    if (!normalized) { setContact(null); return; }
    try {
      const r = await api.get('/voice/contact-lookup', { params: { number: normalized } });
      setContact(r.data?.contact || null);
    } catch {
      setContact(null);
    }
  }, [normalized]);

  useEffect(() => { lookupContact(); setShowSaveForm(false); setEditingName(false); }, [lookupContact]);

  const handleSaveAsCustomer = async () => {
    if (!saveForm.first_name.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/contacts', {
        first_name: saveForm.first_name.trim(),
        last_name: saveForm.last_name.trim(),
        phone: normalized,
        account_type: saveForm.account_type,
        type: 'customer',
        is_customer: true,
      });
      setContact(res.data);
      setShowSaveForm(false);
      toast.success('Customer created');
      onContactChange && onContactChange(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create customer');
    } finally {
      setSaving(false);
    }
  };

  const openEditName = () => {
    setEditForm({ first_name: contact?.first_name || '', last_name: contact?.last_name || '' });
    setEditingName(true);
  };

  const handleUpdateName = async () => {
    if (!contact?.id) return;
    setSaving(true);
    try {
      const res = await api.put(`/contacts/${contact.id}`, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
      });
      setContact(res.data);
      setEditingName(false);
      toast.success('Contact updated');
      onContactChange && onContactChange(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update contact');
    } finally {
      setSaving(false);
    }
  };

  const copyMessageText = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };

  const load = useCallback(async () => {
    if (!normalized) { setMessages([]); return; }
    setLoading(true);
    try {
      const r = await api.get('/voice/sms', { params: { number: normalized, limit: 200 } });
      setMessages(r.data?.messages || []);
    } catch (err) {
      console.error('Failed to load SMS thread', err);
    } finally {
      setLoading(false);
    }
  }, [normalized]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text || !normalized) return;
    setSending(true);
    try {
      await api.post('/voice/sms', { to: normalized, body: text });
      setBody('');
      await load();
      onSent && onSent();
      toast.success('SMS sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send SMS');
    } finally {
      setSending(false);
    }
  };

  if (!number) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-400">
        <div className="text-center max-w-xs">
          <Smartphone className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p>Select a contact or enter a number to start a conversation.</p>
        </div>
      </div>
    );
  }

  const displayName = contact ? (`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.company_name) : contactName;

  return (
    <div className="h-full flex flex-col" data-testid="messaging-chat">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            {editingName ? (
              <div className="flex items-center gap-1.5">
                <input
                  data-testid="sms-edit-first-name"
                  value={editForm.first_name}
                  onChange={e => setEditForm({ ...editForm, first_name: e.target.value })}
                  placeholder="First name"
                  className="w-24 text-xs px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:border-[#003366]/40"
                />
                <input
                  data-testid="sms-edit-last-name"
                  value={editForm.last_name}
                  onChange={e => setEditForm({ ...editForm, last_name: e.target.value })}
                  placeholder="Last name"
                  className="w-24 text-xs px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:border-[#003366]/40"
                />
                <button data-testid="sms-update-name-btn" onClick={handleUpdateName} disabled={saving} className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100">
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button data-testid="sms-cancel-edit-name-btn" onClick={() => setEditingName(false)} className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {displayName || normalized}
                </p>
                {contact && (
                  <button data-testid="sms-edit-name-btn" onClick={openEditName} title="Edit name" className="text-slate-400 hover:text-[#003366]">
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {normalized}
            </p>
          </div>
          {smsStatus?.test_mode && (
            <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200 flex items-center gap-1 flex-shrink-0">
              <AlertTriangle className="w-3 h-3" /> Test mode
            </span>
          )}
        </div>
        {!contact && !showSaveForm && (
          <button
            data-testid="sms-save-as-customer-btn"
            onClick={() => { setSaveForm({ first_name: '', last_name: '', account_type: 'residential' }); setShowSaveForm(true); }}
            className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#003366] bg-[#003366]/5 hover:bg-[#003366]/10 px-2.5 py-1.5 rounded-lg transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" /> Save as Customer
          </button>
        )}
        {!contact && showSaveForm && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="sms-save-customer-form">
            <input
              data-testid="sms-save-first-name"
              value={saveForm.first_name}
              onChange={e => setSaveForm({ ...saveForm, first_name: e.target.value })}
              placeholder="First name *"
              className="w-24 text-xs px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-[#003366]/40"
            />
            <input
              data-testid="sms-save-last-name"
              value={saveForm.last_name}
              onChange={e => setSaveForm({ ...saveForm, last_name: e.target.value })}
              placeholder="Last name"
              className="w-24 text-xs px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-[#003366]/40"
            />
            <select
              data-testid="sms-save-account-type"
              value={saveForm.account_type}
              onChange={e => setSaveForm({ ...saveForm, account_type: e.target.value })}
              className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-[#003366]/40"
            >
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
            </select>
            <button data-testid="sms-save-customer-confirm-btn" onClick={handleSaveAsCustomer} disabled={saving} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-[#003366] text-white hover:bg-[#004080] disabled:opacity-60">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button data-testid="sms-save-customer-cancel-btn" onClick={() => setShowSaveForm(false)} className="text-xs px-2 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100">
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2 bg-slate-50">
        {loading && messages.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-8">Loading...</div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-8">No messages yet</div>
        )}
        {messages.map((m) => {
          const outbound = m.direction === 'outbound';
          const failed = m.status === 'failed';
          return (
            <div key={m.id} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`} data-testid={`sms-bubble-${m.id}`}>
              <div
                title="Click to copy"
                onClick={() => copyMessageText(m.body)}
                className={`group relative max-w-[78%] px-3 py-2 rounded-2xl text-sm shadow-sm cursor-pointer
                ${outbound
                  ? (failed ? 'bg-red-100 text-red-800 rounded-br-md' : 'bg-[#003366] text-white rounded-br-md')
                  : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md'}`}
              >
                <p className="whitespace-pre-wrap break-words pr-4">{m.body}</p>
                <Copy className={`w-3 h-3 absolute top-2 right-2 opacity-0 group-hover:opacity-60 ${outbound ? 'text-white' : 'text-slate-400'}`} />
                <p className={`text-[10px] mt-1 ${outbound ? (failed ? 'text-red-500' : 'text-blue-100') : 'text-slate-400'}`}>
                  {fmtTime(m.created_at)}
                  {failed && ' · failed'}
                  {!failed && outbound && m.status && ` · ${m.status}`}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <form onSubmit={handleSend} className="border-t border-slate-100 p-3 flex items-end gap-2 flex-shrink-0 bg-white">
        <textarea
          data-testid="sms-input"
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Type a message..."
          className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3399FF]/30 focus:border-[#3399FF]"
          disabled={sending}
        />
        <button
          data-testid="sms-send-btn"
          type="submit"
          disabled={sending || !body.trim() || !smsStatus?.configured}
          className="h-10 w-10 flex items-center justify-center rounded-full bg-[#003366] text-white hover:bg-[#004080] disabled:bg-slate-300 disabled:cursor-not-allowed transition"
          title={!smsStatus?.configured ? 'SMS not configured' : 'Send'}
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

